"use client";
import { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { getSupabaseAuthLocalStorageKeys } from "@/lib/supabase-storage-keys";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buyerInfo, setBuyerInfo] = useState(null);

  // Buscar informações do comprador via API (bypass RLS)
  async function fetchBuyerInfo() {
    try {
      const response = await fetch("/api/auth/me");
      if (!response.ok) {
        console.log("Failed to fetch buyer info");
        return null;
      }
      const data = await response.json();
      return data.buyerInfo;
    } catch (err) {
      console.error("Error fetching buyer info:", err);
      return null;
    }
  }

  // Função para atualizar buyerInfo (exposta para componentes)
  async function refreshBuyerInfo() {
    const info = await fetchBuyerInfo();
    setBuyerInfo(info);
    return info;
  }

  // Logout - usa a rota API para limpeza completa server-side
  function logout() {
    // Limpar estado local imediatamente
    setUser(null);
    setBuyerInfo(null);
    
    // Limpar localStorage
    try {
      getSupabaseAuthLocalStorageKeys().forEach((k) => localStorage.removeItem(k));
    } catch (e) {}
    
    // Redirecionar para a rota de logout que limpa cookies no servidor
    window.location.href = "/api/auth/logout";
  }

  // Buscar sessão atual com timeout de segurança
  async function getSession() {
    // Timeout de 5 segundos - se não resolver, força loading=false
    const timeoutId = setTimeout(() => {
      console.log("getSession timeout - forcing loading=false");
      setLoading(false);
    }, 5000);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user) {
        setUser(session.user);
        const info = await fetchBuyerInfo();
        setBuyerInfo(info);
      } else {
        setUser(null);
        setBuyerInfo(null);
      }
    } catch (error) {
      console.error("Error getting session:", error);
      setUser(null);
      setBuyerInfo(null);
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  }

  useEffect(() => {
    getSession();

    // Listener para mudanças de autenticação
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log("Auth state changed:", event);
        if (session?.user) {
          setUser(session.user);
          const info = await fetchBuyerInfo();
          setBuyerInfo(info);
        } else {
          setUser(null);
          setBuyerInfo(null);
        }
        setLoading(false);
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const value = {
    user,
    buyerInfo,
    loading,
    logout,
    refreshBuyerInfo,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth deve ser usado dentro de um AuthProvider");
  }
  return context;
}
